import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  CircularProgress,
  alpha,
  Tooltip,
  IconButton,
  useMediaQuery,
  useTheme,
  Button,
  List,
  ListItem,
  ListItemText,
  Rating,
} from '@mui/material';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDashboardSalonQuery } from '../../../services/dashboardApi';
import moment from 'moment';
import { theme } from '../../../theme';
import DeleteAppointment from '../appointment/deleteAppointment';
import UpdateAppointment from '../appointment/updateAppointment';
import ReviewService from '../../../services/review.service';

// === UTILITAIRES ===
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'confirmed': return theme.colors.success[500];
    case 'pending': return theme.colors.warning[500];
    case 'cancelled': return theme.colors.error[500];
    case 'completed': return theme.colors.info[500];
    default: return theme.colors.primary[500];
  }
};

const getStatusLabel = (status, t) => {
  switch (status?.toLowerCase()) {
    case 'confirmé': case 'confirmed': return t('pro_dashboard.status.confirmed');
    case 'en attente': case 'pending': return t('pro_dashboard.status.pending');
    case 'annulé': case 'cancelled': return t('pro_dashboard.status.cancelled');
    case 'terminé': case 'completed': return t('pro_dashboard.status.completed');
    default: return status || t('pro_dashboard.status.unknown');
  }
};

// === TOOLTIP PRO ===
const AppointmentTooltip = ({ appointment, t }) => {
  const startStr = appointment.start_time_iso || appointment.time || appointment.start_time;
  const endStr = appointment.end_time || appointment.end_time_iso;
  
  const start = typeof startStr === 'string' && startStr.includes('T') 
    ? moment.utc(startStr).local() 
    : moment(startStr, 'HH:mm');
  const end = endStr 
    ? (typeof endStr === 'string' && endStr.includes('T') ? moment.utc(endStr).local() : moment(endStr, 'HH:mm'))
    : start.clone().add(60, 'minutes');
  const duration = end.diff(start, 'minutes');

  return (
    <Box sx={{ p: 1.5, minWidth: 240, fontFamily: 'Roboto, sans-serif' }}>
      <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '1rem', mb: 1, display: 'flex', alignItems: 'center', gap: 0.8 }}>
        <PersonIcon sx={{ fontSize: 18 }} />
        {appointment.client_name || appointment.client}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 4, height: 4, bgcolor: '#fff', borderRadius: '50%' }} />
          <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
            <strong>{t('pro_dashboard.services.title')}:</strong> {appointment.service_name || appointment.service}
          </Typography>
        </Box>

        {appointment.team_member_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ fontSize: 14, color: '#90caf9' }} />
            <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
              <strong>{t('pro_dashboard.team.title')}:</strong> {appointment.team_member_name}
            </Typography>
          </Box>
        )}

        {appointment.salon_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ fontSize: 14, color: '#a5d6a7' }} />
            <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
              <strong>{t('pro_dashboard.salon.title')}:</strong> {appointment.salon_name}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 28,
            height: 28,
            bgcolor: '#fff',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#333',
            fontWeight: 700,
            fontSize: '0.7rem',
          }}>
            {start.format('HH')}
          </Box>
          <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 500 }}>
            {start.format('HH:mm')} - {end.format('HH:mm')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`${duration} min`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, height: 22, fontSize: '0.75rem' }} />
          <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}><strong>{t('pro_dashboard.appointments.duration')}</strong></Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
          <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1, bgcolor: getStatusColor(appointment.status), color: '#fff', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {getStatusLabel(appointment.status, t)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

function SalonDashboard({ salonId }) {
  const { t } = useTranslation();
  const { data, error, isLoading, refetch } = useDashboardSalonQuery({ salonId });

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // États pour les dialogs
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [appointmentToUpdate, setAppointmentToUpdate] = useState(null);
  const [showStats, setShowStats] = useState(false); // Toggle stats visibility
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const activeSalon = data?.salon || null;

  // Charger les avis clients pour ce salon
  useEffect(() => {
    const loadReviews = async () => {
      if (!salonId) return;
      setReviewsLoading(true);
      try {
        const data = await ReviewService.getSalonReviews(salonId);
        const items = Array.isArray(data) ? data : data?.results || [];
        setReviews(items);
      } catch (error) {
        // Échec silencieux : on log seulement en console pour ne pas afficher d'erreur à l'utilisateur
        console.error('Error loading salon reviews in SalonDashboard:', error);
        setReviews([]);
      } finally {
        setReviewsLoading(false);
      }
    };

    loadReviews();
  }, [salonId]);

  // Gestionnaires d'événements pour les rendez-vous
  const handleEditAppointment = (appointment) => {
    console.log('Appointment data from salon dashboard:', appointment);

    const formattedAppointment = {
      appointment_id: appointment.id || appointment.appointment_id,
      start_time: appointment.start_time_iso || appointment.start_time,
      end_time: appointment.end_time || appointment.end_time_iso,
      status: appointment.status || 'pending',
      // Les IDs doivent être extraits correctement
      client: appointment.client_id || appointment.client || null,
      team_member: appointment.team_member_id || appointment.team_member || null,
      service: appointment.service_id || appointment.service || null,
      salon: appointment.salon_id || appointment.salon || null,
      // Les noms pour l'affichage
      client_name: appointment.client_name || appointment.client,
      team_member_name: appointment.team_member_name,
      service_name: appointment.service_name || appointment.service,
      salon_name: appointment.salon_name
    };

    console.log('Formatted appointment for update:', formattedAppointment);

    setAppointmentToUpdate(formattedAppointment);
    setIsUpdateDialogOpen(true);
  };

  const handleDeleteAppointment = (appointment) => {
    setAppointmentToDelete(appointment);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseUpdateDialog = () => {
    setIsUpdateDialogOpen(false);
    setAppointmentToUpdate(null);
    refetch();
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setAppointmentToDelete(null);
    refetch();
  };

  // === POSITIONNEMENT RDV (IDENTIQUE À GLOBALDASHBOARD) ===
  const positionedAppointments = useMemo(() => {
    const START_HOUR = 8;
    const HOUR_HEIGHT = 60;
    const todayAppointments = data?.today_appointments || [];

    return todayAppointments
      .map(a => {
        const startStr = a.start_time_iso || a.time || a.start_time;
        const endStr = a.end_time || a.end_time_iso;

        const start = typeof startStr === 'string' && startStr.includes('T') 
          ? moment.utc(startStr).local() 
          : moment(startStr, 'HH:mm');
        const end = endStr 
          ? (typeof endStr === 'string' && endStr.includes('T') ? moment.utc(endStr).local() : moment(endStr, 'HH:mm'))
          : start.clone().add(60, 'minutes');

        if (!start.isValid()) return null;

        const duration = end.diff(start, 'minutes');
        const top = ((start.hour() - START_HOUR) + start.minute() / 60) * HOUR_HEIGHT;
        const height = Math.max((duration / 60) * HOUR_HEIGHT, 30);

        return {
          ...a,
          start_time: start,
          end_time: end,
          topPosition: top,
          height,
          durationMinutes: duration,
          client_name: a.client_name || a.client,
          service_name: a.service_name || a.service,
          status: a.status || 'confirmed',
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.topPosition - b.topPosition);
  }, [data?.today_appointments]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error.main" variant="h6">
          {t('pro_dashboard.common.error')}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
          {t('pro_dashboard.dashboard.try_again')}
        </Typography>
      </Box>
    );
  }

  if (!activeSalon) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">{t('pro_dashboard.dashboard.no_salon_found')}</Typography>
        <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
          {t('pro_dashboard.dashboard.select_valid_salon')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Salon info banner */}
      <Box 
        sx={{ 
          bgcolor: theme.colors.secondary[200], 
          borderBottom: '1px solid',
          borderColor: theme.colors.secondary[200],
          px: 3,
          py: 1.5,
          mb: 3
        }}
      >
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BusinessIcon sx={{ color: theme.colors.secondary[600] }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: theme.colors.secondary[600] }}>
                  {activeSalon?.name || t('pro_dashboard.salon.title')}
                </Typography>
                {activeSalon?.address && (
                  <Typography variant="body2" sx={{ color: theme.colors.secondary[500] }}>
                    {activeSalon.address}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: theme.colors.secondary[500] }}>
                <GroupsIcon fontSize="small" />
                <Typography variant="body2">
                  {activeSalon?.staff_count || 0} {t('pro_dashboard.dashboard.employees')}
                </Typography>
              </Box>
              <Chip 
                label={activeSalon?.status || t('pro_dashboard.status.active')} 
                size="small" 
                sx={{ 
                  bgcolor: theme.colors.secondary[50], 
                  color: theme.colors.secondary[500],
                  fontWeight: 500
                }} 
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Toggle button for mobile stats */}
      {isMobile && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setShowStats(!showStats)}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              textTransform: 'none',
              borderColor: theme.colors.primary[300],
              color: theme.colors.primary[700],
              '&:hover': {
                borderColor: theme.colors.primary[400],
                backgroundColor: alpha(theme.colors.primary[50], 0.5)
              }
            }}
          >
            {showStats ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            {showStats ? t('pro_dashboard.dashboard.hide_stats') : t('pro_dashboard.dashboard.show_stats')}
          </Button>
        </Box>
      )}

      {/* Stats Cards - Hidden on mobile when showStats is false */}
      {(!isMobile || showStats) && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Cartes de stats (inchangées) */}
        <Grid item xs={12} sm={3}>
          <Card elevation={0} sx={{ 
            position: 'relative', overflow: 'hidden', borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.colors.success[100]} 0%, ${theme.colors.success[200]} 100%)`,
            border: 'none', height: 160, transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(34, 197, 94, 0.25)' }
          }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: 2, background: `linear-gradient(135deg, ${theme.colors.success[500]} 0%, ${theme.colors.success[600]} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${alpha(theme.colors.success[500], 0.4)}`, mb: 1.5 }}>
                <TrendingUpIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Typography variant="caption" sx={{ color: theme.colors.success[800], fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                {t('pro_dashboard.dashboard.daily_revenue')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: theme.colors.success[900], lineHeight: 1.2 }}>
                {(data?.daily_revenue !== undefined ? data.daily_revenue : (data?.today_metrics?.revenue ? parseFloat(data.today_metrics.revenue) : 0))} <Typography component="span" variant="h6" sx={{ color: theme.colors.success[700] }}>DA</Typography>
              </Typography>
              <Typography variant="caption" sx={{ color: ((data?.daily_revenue_change ?? 0) >= 0) ? theme.colors.success[700] : theme.colors.error[700], fontWeight: 600, mt: 0.5 }}>
                {(data?.daily_revenue_change ?? 0) >= 0 ? '+' : ''}{data?.daily_revenue_change ?? 0}% {t('pro_dashboard.dashboard.vs_yesterday')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Card elevation={0} sx={{ 
            position: 'relative', overflow: 'hidden', borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.colors.warning[100]} 0%, ${theme.colors.warning[200]} 100%)`,
            border: 'none', height: 160, transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(245, 158, 11, 0.25)' }
          }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: 2, background: `linear-gradient(135deg, ${theme.colors.warning[500]} 0%, ${theme.colors.warning[600]} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${alpha(theme.colors.warning[500], 0.4)}`, mb: 1.5 }}>
                <CalendarMonthIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Typography variant="caption" sx={{ color: theme.colors.warning[800], fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                {t('pro_dashboard.dashboard.stats.today_appointments')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: theme.colors.warning[900], lineHeight: 1.2 }}>
                {data?.appointments_today?.total ?? (data?.today_metrics?.appointments_count ? parseInt(String(data.today_metrics.appointments_count)) : 0)}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.colors.warning[700], display: 'block', mt: 0.5 }}>
                {data?.appointments_today?.confirmed ?? (data?.today_metrics?.appointments_confirmed ? parseInt(String(data.today_metrics.appointments_confirmed)) : 0)} {t('pro_dashboard.status.confirmed')}, {data?.appointments_today?.pending ?? (data?.today_metrics?.appointments_waiting ? parseInt(String(data.today_metrics.appointments_waiting)) : 0)} {t('pro_dashboard.status.pending')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Card elevation={0} sx={{ 
            position: 'relative', overflow: 'hidden', borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.colors.warning[100]} 0%, ${theme.colors.warning[200]} 100%)`,
            border: 'none', height: 160, transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(245, 158, 11, 0.25)' }
          }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: 2, background: `linear-gradient(135deg, ${theme.colors.warning[500]} 0%, ${theme.colors.warning[600]} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${alpha(theme.colors.warning[500], 0.4)}`, mb: 1.5 }}>
                <GroupsIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Typography variant="caption" sx={{ color: theme.colors.warning[800], fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                {t('pro_dashboard.dashboard.stats.total_clients')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: theme.colors.warning[900], lineHeight: 1.2 }}>
                {data?.monthly_clients ?? (data?.today_metrics?.clients_count ? parseInt(String(data.today_metrics.clients_count)) : 0)}
              </Typography>
              <Typography variant="caption" sx={{ color: ((data?.monthly_clients_change ?? 0) >= 0) ? theme.colors.success[700] : theme.colors.error[700], fontWeight: 600, mt: 0.5 }}>
                {(data?.monthly_clients_change ?? 0) >= 0 ? '+' : ''}{data?.monthly_clients_change ?? 0}% {t('pro_dashboard.dashboard.vs_last_month')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Card elevation={0} sx={{ 
            position: 'relative', overflow: 'hidden', borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.colors.success[100]} 0%, ${theme.colors.success[200]} 100%)`,
            border: 'none', height: 160, transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(34, 197, 94, 0.25)' }
          }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: 2, background: `linear-gradient(135deg, ${theme.colors.success[500]} 0%, ${theme.colors.success[600]} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${alpha(theme.colors.success[500], 0.4)}`, mb: 1.5 }}>
                <BarChartIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Typography variant="caption" sx={{ color: theme.colors.success[800], fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                {t('pro_dashboard.dashboard.occupancy_rate')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: theme.colors.success[900], lineHeight: 1.2 }}>
                {(data?.occupancy_rate !== undefined ? data.occupancy_rate : (data?.today_metrics?.occupation_rate ? parseFloat(data.today_metrics.occupation_rate) : 0))}<Typography component="span" variant="h6" sx={{ color: theme.colors.success[700] }}>%</Typography>
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(data?.occupancy_rate !== undefined ? data.occupancy_rate : (data?.today_metrics?.occupation_rate ? parseFloat(data.today_metrics.occupation_rate) : 0))} 
                sx={{ height: 6, borderRadius: 3, backgroundColor: alpha(theme.colors.success[500], 0.2), mt: 0.5, width: '80%', '& .MuiLinearProgress-bar': { borderRadius: 3, background: `linear-gradient(90deg, ${theme.colors.success[600]} 0%, ${theme.colors.success[700]} 100%)` } }} 
              />
            </CardContent>
          </Card>
        </Grid>

        </Grid>
      )}

      {/* Main Content Layout */}
      <Grid container spacing={3}>
        {/* === RENDEZ-VOUS D'AUJOURD'HUI - MÊME VISUEL QUE GLOBALDASHBOARD === */}
        <Grid item xs={12} md={9}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              border: `1px solid ${theme.colors.secondary[100]}`,
              overflow: 'hidden'
            }}
          >
            <CardHeader
              sx={{
                background: `linear-gradient(135deg, ${theme.colors.secondary[50]} 0%, ${theme.colors.background.primary} 100%)`,
                borderBottom: `1px solid ${theme.colors.secondary[100]}`
              }}
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.colors.secondary[400]} 0%, ${theme.colors.secondary[500]} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 12px ${theme.colors.secondary[200]}`,
                    }}
                  >
                    <AccessTimeIcon sx={{ color: 'white', fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: theme.colors.text.primary }}>
                      {t('pro_dashboard.dashboard.stats.today_appointments')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                      {activeSalon?.name || t('pro_dashboard.salon.title')}
                    </Typography>
                  </Box>
                </Box>
              }
            />
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ position: 'relative', minHeight: 720, backgroundColor: '#fafafa' }}>
                {/* Grille des heures */}
                {Array.from({ length: 13 }, (_, i) => i + 8).map(hour => (
                  <Box
                    key={hour}
                    sx={{
                      position: 'absolute',
                      top: `${(hour - 8) * 60}px`,
                      left: 0,
                      right: 0,
                      height: 60,
                      borderTop: `1px solid ${theme.colors.primary[200]}`,
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                ))}

                {/* Demi-heures */}
                {Array.from({ length: 12 }, (_, i) => i + 8).map(hour => (
                  <Box
                    key={`half-${hour}`}
                    sx={{
                      position: 'absolute',
                      top: `${(hour - 8) * 60 + 29}px`,
                      left: 0,
                      right: 0,
                      borderTop: `1px dashed ${theme.colors.primary[100]}`,
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                ))}

                {/* Colonne des heures */}
                <Box sx={{ position: 'absolute', left: 0, top: 0, width: 80, backgroundColor: '#fff', zIndex: 5, borderRight: `1px solid ${theme.colors.primary[200]}` }}>
                  {Array.from({ length: 13 }, (_, i) => i + 8).map(hour => (
                    <Box key={hour} sx={{ height: 60, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 1 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: theme.colors.text.secondary, fontWeight: 500 }}>
                        {`${hour}:00`}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Rendez-vous */}
                <Box sx={{ position: 'relative', paddingLeft: '80px', paddingRight: '16px' }}>
                  {positionedAppointments.map((a, i) => (
                    <Tooltip
                      key={`${a.id}-${i}`}
                      title={<AppointmentTooltip appointment={a} t={t} />}
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: '#1a1a1a',
                            color: '#fff',
                            borderRadius: 2,
                            boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                            border: '1px solid #333',
                            p: 0,
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: `${a.topPosition}px`,
                          left: 80,
                          right: 0,
                          height: `${a.height}px`,
                          minHeight: 30,
                          backgroundColor: getStatusColor(a.status),
                          border: `1px solid ${getStatusColor(a.status)}`,
                          borderLeft: `4px solid ${getStatusColor(a.status)}`,
                          borderRadius: 1,
                          p: 1,
                          cursor: 'pointer',
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                          zIndex: 2,
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            zIndex: 10,
                            '& .actions': { opacity: 1 },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            borderRadius: 0.5,
                            p: 1,
                            height: '100%',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: a.durationMinutes <= 30 ? 'center' : 'flex-start',
                            alignItems: 'flex-start',
                          }}
                        >
                          {a.durationMinutes <= 30 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.8rem',
                                  color: theme.colors.text.primary,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  flex: 1,
                                }}
                              >
                                {a.client_name}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: theme.colors.text.secondary }}>
                                {a.start_time.format('HH:mm')} • {a.durationMinutes} min
                              </Typography>
                            </Box>
                          ) : (
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1, alignItems: 'center' }}>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    color: theme.colors.text.primary,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    flex: 1,
                                  }}
                                >
                                  {a.client_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                                  <Typography sx={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                                    {a.start_time.format('HH:mm')} - {a.end_time.format('HH:mm')}
                                  </Typography>
                                  <Chip
                                    label={`${a.durationMinutes} min`}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.65rem', backgroundColor: theme.colors.grey[100], color: theme.colors.text.secondary }}
                                  />
                                  <Chip
                                    label={getStatusLabel(a.status, t)}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.65rem', backgroundColor: getStatusColor(a.status), color: 'white' }}
                                  />
                                </Box>
                              </Box>

                              {a.height > 50 && (
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', width: '100%' }}>
                                  <Typography sx={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                                    {a.service_name}
                                  </Typography>
                                  {a.team_member_name && (
                                    <Chip
                                      icon={<PersonIcon sx={{ fontSize: 10 }} />}
                                      label={a.team_member_name}
                                      size="small"
                                      sx={{ height: 20, fontSize: '0.7rem', backgroundColor: theme.colors.primary[100], color: theme.colors.primary[700] }}
                                    />
                                  )}
                                  {a.salon_name && (
                                    <Chip
                                      icon={<BusinessIcon sx={{ fontSize: 10 }} />}
                                      label={a.salon_name}
                                      size="small"
                                      sx={{ height: 20, fontSize: '0.7rem', backgroundColor: theme.colors.secondary[100], color: theme.colors.secondary[700] }}
                                    />
                                  )}
                                </Box>
                              )}
                            </>
                          )}

                          <Box
                            className="actions"
                            sx={{
                              position: 'absolute',
                              top: 6,
                              right: 6,
                              display: 'flex',
                              gap: 0.3,
                              opacity: 0,
                              transition: 'opacity 0.2s',
                            }}
                          >
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditAppointment(a);
                              }}
                              sx={{ p: '2px', backgroundColor: 'rgba(255,255,255,0.95)', width: 20, height: 20, color: theme.colors.warning[600] }}
                            >
                              <EditIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAppointment(a);
                              }}
                              sx={{ p: '2px', backgroundColor: 'rgba(255,255,255,0.95)', width: 20, height: 20, color: theme.colors.error[600] }}
                            >
                              <DeleteIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* === COLONNE DROITE === */}
        <Grid item xs={12} md={3}>
          <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${theme.colors.success[100]}`, overflow: 'hidden', background: `linear-gradient(135deg, ${theme.colors.success[50]} 0%, ${theme.colors.background.primary} 100%)` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 2, background: `linear-gradient(135deg, ${theme.colors.success[500]} 0%, ${theme.colors.success[600]} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 16px ${theme.colors.success[200]}` }}>
                  <TrendingUpIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t('pro_dashboard.dashboard.stats.total_revenue')} {data?.current_month ? `- ${data.current_month}` : t('pro_dashboard.dashboard.this_month')}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                    {(data?.monthly_revenue !== undefined ? data.monthly_revenue : (data?.monthly_performance?.monthly_revenue ? parseFloat(data.monthly_performance.monthly_revenue) : 0))} <Typography component="span" variant="body1" sx={{ color: theme.colors.text.tertiary }}>DA</Typography>
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="body2" sx={{ color: ((data?.monthly_revenue_change ?? 0) >= 0) ? theme.colors.success[600] : theme.colors.error[600], fontWeight: 600 }}>
                  {(data?.monthly_revenue_change ?? 0) >= 0 ? '+' : ''}{data?.monthly_revenue_change ?? 0}%
                </Typography>
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                  {t('pro_dashboard.dashboard.vs_last_month')} {data?.previous_month ? data.previous_month : t('pro_dashboard.dashboard.previous_month')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>{t('pro_dashboard.dashboard.monthly_target')}</Typography>
                <Typography variant="body2" fontWeight={600}>{(data?.monthly_target !== undefined ? data.monthly_target : (data?.monthly_performance?.monthly_target ? parseFloat(data.monthly_performance.monthly_target) : 0))} DA</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(data?.monthly_target_progress !== undefined ? data.monthly_target_progress : (data?.monthly_performance?.percentage_of_target ? parseFloat(data.monthly_performance.percentage_of_target) : 0))} 
                sx={{ height: 6, borderRadius: 3, backgroundColor: theme.colors.success[100], '& .MuiLinearProgress-bar': { borderRadius: 3, background: `linear-gradient(90deg, ${theme.colors.success[500]} 0%, ${theme.colors.success[600]} 100%)` } }} 
              />
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary, mt: 1, display: 'block' }}>
                {(data?.monthly_target_progress !== undefined ? data.monthly_target_progress : (data?.monthly_performance?.percentage_of_target ? parseFloat(data.monthly_performance.percentage_of_target) : 0))}% {t('pro_dashboard.dashboard.target_reached')}
              </Typography>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.colors.info[100]}`, overflow: 'hidden' }}>
            <CardHeader 
              sx={{
                background: `linear-gradient(135deg, ${theme.colors.info[50]} 0%, ${theme.colors.background.primary} 100%)`,
                borderBottom: `1px solid ${theme.colors.info[100]}`,
              }}
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, background: `linear-gradient(135deg, ${theme.colors.info[500]} 0%, ${theme.colors.info[600]} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${theme.colors.info[200]}` }}>
                    <TrendingUpIcon sx={{ color: 'white', fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary }}>
                      {t('pro_dashboard.dashboard.popular_services')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                      {t('pro_dashboard.dashboard.top_services')}
                    </Typography>
                  </Box>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                {data?.popular_services ? (
                  data.popular_services.map((item) => (
                    <Box key={item.service_name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">{item.service_name}</Typography>
                      <Chip label={`${item.percentage}%`} size="small" variant="outlined" />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ textAlign: 'center', color: theme.colors.text.secondary }}>
                    {t('pro_dashboard.dashboard.no_data')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ mt: 3, borderRadius: 3, border: `1px solid ${theme.colors.primary[100]}`, overflow: 'hidden' }}>
            <CardHeader
              sx={{
                background: `linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.background.primary} 100%)`,
                borderBottom: `1px solid ${theme.colors.primary[100]}`,
              }}
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 12px ${theme.colors.primary[200]}`,
                    }}
                  >
                    <BarChartIcon sx={{ color: 'white', fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary }}>
                      {t('pro_dashboard.dashboard.customer_reviews')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                      {t('pro_dashboard.dashboard.average_rating')}
                    </Typography>
                  </Box>
                </Box>
              }
            />
            <CardContent>
              {reviewsLoading && (
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                  {t('pro_dashboard.dashboard.loading_reviews')}
                </Typography>
              )}

              {!reviewsLoading && reviews && reviews.length === 0 && (
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                  {t('pro_dashboard.dashboard.no_reviews')}
                </Typography>
              )}

              {!reviewsLoading && reviews && reviews.length > 0 && (
                <>
                  {(() => {
                    const averageRating =
                      reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Rating
                          name="salon-rating-pro"
                          value={averageRating}
                          precision={0.5}
                          readOnly
                          size="small"
                        />
                        <Typography
                          variant="body2"
                          sx={{ ml: 1, color: theme.colors.text.secondary }}
                        >
                          {averageRating.toFixed(1)} / 5 · {reviews.length} {t('pro_dashboard.dashboard.reviews')}
                        </Typography>
                      </Box>
                    );
                  })()}

                  <List dense sx={{ p: 0 }}>
                    {reviews.slice(0, 5).map((review) => (
                      <ListItem
                        key={review.review_id}
                        alignItems="flex-start"
                        disableGutters
                        sx={{ pb: 1.5 }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">
                                {review.client_name || 'Client'}
                              </Typography>
                              <Rating
                                value={review.rating}
                                readOnly
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <>
                              {review.comment && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: theme.colors.text.secondary }}
                                >
                                  {review.comment}
                                </Typography>
                              )}
                              {review.created_at && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: theme.colors.grey[600] }}
                                >
                                  {new Date(review.created_at).toLocaleDateString('fr-FR')}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs pour modifier et supprimer */}
      {appointmentToUpdate && (
        <UpdateAppointment
          open={isUpdateDialogOpen}
          onClose={handleCloseUpdateDialog}
          onSuccess={handleCloseUpdateDialog}
          appointment={appointmentToUpdate}
        />
      )}

      {appointmentToDelete && (
        <DeleteAppointment
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onSuccess={handleCloseDeleteDialog}
          appointment={appointmentToDelete}
        />
      )}
    </Box>
  );
}

export default SalonDashboard;